import api from '../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Row,
  Col,
  Statistic,
  Tag,
  Tooltip,
  message,
  Spin,
  DatePicker,
  Select,
  Typography,
  Progress,
  Divider,
  Tabs,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CreditCardOutlined,
  WalletOutlined,
  // ReceiptOutlined,
  BankOutlined,
  BarChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import config from "../../config";
import { usePermissions } from "../../permissions";
import PaymentConfirmationModal from "./PaymentConfirmationModal";
import { FaReceipt } from "react-icons/fa";
import { formatKES } from "../../utils/currency";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const TransactionsTable = () => {
  const account = useSelector((state) => state.account);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("metrics");
  const [metrics, setMetrics] = useState({
    totalDebit: 0,
    totalCredit: 0,
    totalReceivables: 0,
    totalPayables: 0,
    totalCount: 0,
    paidCount: 0,
    unpaidCount: 0,
    partiallyPaidCount: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalBalance: 0,
    confirmed: 0,
    amountConfirmed: 0,
    invoiced: 0,
    amountInvoiced: 0,
    due: 0,
    amountDue: 0,
  });
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const { viewPermission } = usePermissions();
  
  // Date range state
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "month"),
    dayjs(),
  ]);

  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Fetch chart of accounts for display
  
  useEffect(() => {
    const fetchChartOfAccounts = async () => {
      try {
        const { data } = await api.get(`/api/chart-of-accounts`);
        setChartOfAccounts(data);
      } catch (err) {
        console.error("Failed to fetch chart of accounts", err);
      }
    };
    fetchChartOfAccounts();
  }, [api]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        startDate: dateRange[0]?.format("YYYY-MM-DD"),
        endDate: dateRange[1]?.format("YYYY-MM-DD"),
        type: typeFilter,
        paymentStatus: statusFilter,
      };
      const { data } = await api.get(`/api/transactions`, { params });
      setTransactions(data.transactions || []);
      setMetrics(data.metrics || {
        totalDebit: 0,
        totalCredit: 0,
        totalReceivables: 0,
        totalPayables: 0,
        totalCount: 0,
        paidCount: 0,
        unpaidCount: 0,
        partiallyPaidCount: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalBalance: 0,
        confirmed: 0,
        amountConfirmed: 0,
        invoiced: 0,
        amountInvoiced: 0,
        due: 0,
        amountDue: 0,
      });
    } catch (err) {
      message.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  }, [api, dateRange, typeFilter, statusFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleView = (record) => {
    setSelectedTransaction(record);
    setModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "fully_paid":
        return "success";
      case "partially_paid":
        return "warning";
      case "unpaid":
        return "error";
      case "cash":
        return "processing";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "fully_paid":
        return "Fully Paid";
      case "partially_paid":
        return "Partially Paid";
      case "unpaid":
        return "Unpaid";
      case "cash":
        return "Cash";
      default:
        return status || "Unknown";
    }
  };

  const getAccountName = (glCode) => {
    const account = chartOfAccounts.find((acc) => acc.gl_code === glCode);
    return account ? `${glCode} - ${account.account_name}` : glCode || "—";
  };

  const totalAmount = metrics.totalAmount || metrics.totalDebit + metrics.totalCredit || 0;

  const paymentProgress = totalAmount > 0 
    ? (metrics.totalPaid / totalAmount) * 100 
    : 0;

  const columns = [
    {
      title: "Date",
      dataIndex: "recorded",
      key: "date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
      sorter: (a, b) => dayjs(a.recorded).unix() - dayjs(b.recorded).unix(),
    },
    {
      title: "Transaction ID",
      dataIndex: "transaction_code",
      key: "code",
      sorter: true,
    },
    {
      title: "Description",
      dataIndex: "transactionDescription",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Amount (KES)",
      dataIndex: "toPay",
      key: "amount",
      align: "right",
      render: (val) => val?.toLocaleString() || "0.00",
      sorter: (a, b) => (a.toPay || 0) - (b.toPay || 0),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: "GL Debit",
      dataIndex: "gl_debit",
      key: "gl_debit",
      render: (gl) => <Tooltip title={getAccountName(gl)}>{gl || "—"}</Tooltip>,
    },
    {
      title: "GL Credit",
      dataIndex: "gl_credit",
      key: "gl_credit",
      render: (gl) => <Tooltip title={getAccountName(gl)}>{gl || "—"}</Tooltip>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space>
          {viewPermission && (
            <Tooltip title="View Details">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleView(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Filter transactions by search
  const filteredTransactions = transactions.filter((item) =>
    searchText
      ? item.transaction_code?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.transactionDescription?.toLowerCase().includes(searchText.toLowerCase())
      : true
  );

  // Metric cards configuration
  const metricCards = [
    {
      title: "Total Debit",
      value: formatKES(metrics.totalDebit),
      prefix: "KES",
      icon: <CreditCardOutlined />,
      // color: "#1677ff",
      // bgColor: "#e6f7ff",
    },
    {
      title: "Total Credit",
      value: formatKES(metrics.totalCredit),
      prefix: "KES",
      icon: <BankOutlined />,
      // color: "#52c41a",
      // bgColor: "#f6ffed",
    },
    {
      title: "Receivables",
      value: formatKES(metrics.totalReceivables),
      prefix: "KES",
      icon: <FaReceipt />,
      // color: "#faad14",
      // bgColor: "#fffbe6",
    },
    {
      title: "Payables",
      value: formatKES(metrics.totalPayables),
      prefix: "KES",
      icon: <WalletOutlined />,
      // color: "#722ed1",
      // bgColor: "#f9f0ff",
    },
    {
      title: "Total Amount",
      value: formatKES(metrics.totalAmount || metrics.totalDebit + metrics.totalCredit || 0),
      prefix: "KES",
      icon: <DollarOutlined />,
      // color: "#13c2c2",
      // bgColor: "#e6fffb",
    },
    {
      title: "Total Paid",
      value: formatKES(metrics.totalPaid),
      prefix: "KES",
      icon: <CheckCircleOutlined />,
      // color: "#52c41a",
      // bgColor: "#f6ffed",
    },
    {
      title: "Balance Due",
      value: formatKES(metrics.totalBalance),
      prefix: "KES",
      icon: <ExclamationCircleOutlined />,
      // color: "#faad14",
      // bgColor: "#fffbe6",
    },
    {
      title: "Transactions",
      value: metrics.totalCount,
      icon: <FileTextOutlined />,
      // color: "#1677ff",
      // bgColor: "#e6f7ff",
    },
  ];

  // Summary metrics for small cards
  const summaryCards = [
    {
      title: "Confirmed",
      value: metrics.confirmed,
      subtitle: `KES ${metrics.amountConfirmed?.toLocaleString()}`,
      icon: <CheckCircleOutlined />,
      color: "#52c41a",
    },
    {
      title: "Invoiced",
      value: metrics.invoiced,
      subtitle: `KES ${metrics.amountInvoiced?.toLocaleString()}`,
      icon: <FileTextOutlined />,
      color: "#1677ff",
    },
    {
      title: "Due",
      value: metrics.due,
      subtitle: `KES ${metrics.amountDue?.toLocaleString()}`,
      icon: <ExclamationCircleOutlined />,
      color: "#faad14",
    },
  ];

  return (
    <>

      {/* Tabs for Metrics and Table */}
      <Card style={{ marginBottom: 24 }} title="Financial Transactions" extra={ <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates)}
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Transaction Type"
              allowClear
              style={{ width: "100%" }}
              value={typeFilter || undefined}
              onChange={setTypeFilter}
            >
              <Option value="">All Types</Option>
              <Option value="sales">Sales</Option>
              <Option value="purchases">Purchases</Option>
              <Option value="expenses">Expenses</Option>
              <Option value="payments">Payments</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Payment Status"
              allowClear
              style={{ width: "100%" }}
              value={statusFilter || undefined}
              onChange={setStatusFilter}
            >
              <Option value="">All Status</Option>
              <Option value="fully_paid">Fully Paid</Option>
              <Option value="partially_paid">Partially Paid</Option>
              <Option value="unpaid">Unpaid</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Space>
              <Button
                type="primary"
                onClick={fetchTransactions}
                loading={loading}
              >
                Apply Filters
              </Button>
              <Button
                onClick={() => {
                  setDateRange([dayjs().subtract(1, "month"), dayjs()]);
                  setTypeFilter("");
                  setStatusFilter("");
                  setSearchText("");
                }}
              >
                Reset
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchTransactions}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>}>
     
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        // type="card"
        items={[
          {
            key: "metrics",
            label: (
              <span>
                <BarChartOutlined />
                Metrics Dashboard
              </span>
            ),
            children: (
              <Spin spinning={loading}>
                {/* Payment Progress */}
                <Card style={{ marginBottom: 24 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Payment Progress"
                        value={paymentProgress.toFixed(2)}
                        suffix="%"
                        valueStyle={{ color: paymentProgress >= 70 ? "#52c41a" : "#faad14" }}
                      />
                    </Col>
                    <Col span={16}>
                      <Progress
                        percent={paymentProgress.toFixed(2)}
                        strokeColor={{
                          "0%": "#108ee9",
                          "100%": "#87d068",
                        }}
                        status={paymentProgress >= 100 ? "success" : "active"}
                      />
                    </Col>
                  </Row>
                  <Divider style={{ margin: "16px 0" }} />
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text type="secondary">Paid Transactions</Text>
                      <div><Text strong>{metrics.paidCount}</Text></div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">Unpaid Transactions</Text>
                      <div><Text strong>{metrics.unpaidCount}</Text></div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">Partially Paid</Text>
                      <div><Text strong>{metrics.partiallyPaidCount}</Text></div>
                    </Col>
                  </Row>
                </Card>

                {/* Main Metrics Cards - 4 per row */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  {metricCards.slice(0, 4).map((card, idx) => (
                    <Col xs={24} sm={12} md={6} key={idx}>
                      <Card style={{ backgroundColor: card.bgColor }}>
                        <Statistic
                          title={card.title}
                          value={card.value?.toLocaleString()}
                          prefix={card.icon}
                          valueStyle={{ color: card.color, fontSize: 20 }}
                        />
                        {/* <div style={{ marginTop: 8, color: card.color }}>
                          {card.icon}
                        </div> */}
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  {metricCards.slice(4, 8).map((card, idx) => (
                    <Col xs={24} sm={12} md={6} key={idx}>
                      <Card style={{ backgroundColor: card.bgColor }}>
                        <Statistic
                          title={card.title}
                          value={card.value?.toLocaleString()}
                          prefix={card.icon}
                          valueStyle={{ color: card.color, fontSize: 20 }}
                        />
                        {/* <div style={{ marginTop: 8, color: card.color }}>
                          {card.icon}
                        </div> */}
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Summary Cards */}
                <Card title="Transaction Summary">
                  <Row gutter={[16, 16]}>
                    {summaryCards.map((card, idx) => (
                      <Col xs={24} sm={12} md={8} key={idx}>
                        <Statistic
                          title={card.title}
                          value={card.value}
                          valueStyle={{ color: card.color }}
                        />
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary">{card.subtitle}</Text>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Spin>
            ),
          },
          {
            key: "table",
            label: (
              <span>
                <TableOutlined />
                Transaction List
              </span>
            ),
            children: (
              <Card
                title="Transaction History"
                extra={
                  <Input.Search
                    placeholder="Search by ID or description..."
                    allowClear
                    style={{ width: 260 }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                  />
                }
              >
                <Table
                  columns={columns}
                  dataSource={filteredTransactions}
                  rowKey={(record) => record.transactionRecordId || record.id}
                  loading={loading}
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} transactions`,
                  }}
                  size="small"
                  scroll={{ x: "max-content" }}
                />
              </Card>
            ),
          },
        ]}
      /> </Card>

      {/* Payment Confirmation Modal */}
      <PaymentConfirmationModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedTransaction(null);
        }}
        onConfirm={() => {
          setModalVisible(false);
          setSelectedTransaction(null);
          fetchTransactions();
        }}
        transaction={selectedTransaction}
      />
    </>
  );
};

export default TransactionsTable;